import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Box,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Paper from 'components/@extended/Paper'
import ComponentDisplay from 'components/ComponentDisplay'
import ComponentField from 'components/ComponentField'
import { useCreatePlanContext } from 'contexts/CreatePlanContextProvider'
import { useState } from 'react'
import type { Component, TargetDTO, CompositeTargetValue, CompositeTargetValueItem } from 'types/components'
import { ComponentType, ComponentSourceType, unformatValue, getCompositeFunction } from 'types/components'
import { createIdLabelMap } from 'types/periods'

interface PlanTargetRowProps {
  componentId: string
  component?: Component
  target?: TargetDTO
  reviewOnly?: boolean
  periodLabel?: string
  handleUpdateTarget: (value: string | number, compositeId: string) => void
  handleUpdateCompositeTarget?: (compositeId: string, compositeValues: CompositeTargetValueItem[]) => void
}

const PlanTargetRow: React.FC<PlanTargetRowProps> = ({
  componentId,
  component,
  target,
  reviewOnly,
  periodLabel,
  handleUpdateTarget,
  handleUpdateCompositeTarget,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(false)

  if (component == null || target == null) {
    return <></>
  }

  const isComposite = component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE

  // Parse composite values if it's a composite component
  const parseCompositeValue = (): { values: CompositeTargetValueItem[]; overallValue: string } => {
    if (!isComposite || !target.value) return { values: [], overallValue: '0' }

    try {
      const parsed = JSON.parse(target.value) as CompositeTargetValue
      return {
        values: parsed.compositeTargetValues || [],
        overallValue: parsed.value || '0',
      }
    } catch {
      return { values: [], overallValue: '0' }
    }
  }

  const { values: compositeValues, overallValue } = parseCompositeValue()

  const handleCompositeValueChange = (valueIndex: number, field: 'target', newValue: string) => {
    if (!handleUpdateCompositeTarget) return

    const updatedValues = compositeValues.map((item, index) =>
      index === valueIndex ? { ...item, [field]: newValue } : item
    )

    const compositeId = `${componentId}_${target.periodId}`
    handleUpdateCompositeTarget(compositeId, updatedValues)
  }

  const handleOverallTargetChange = (newValue: string) => {
    const compositeId = `${componentId}_${target.periodId}`

    // Create a new composite value with the updated overall value but same composite values
    const updatedCompositeValue: CompositeTargetValue = {
      value: newValue,
      compositeTargetValues: compositeValues,
    }

    // Update the target directly with the new overall value
    handleUpdateTarget(JSON.stringify(updatedCompositeValue), compositeId)
  }

  // Use ComponentDisplay for range display
  const rangeDisplay =
    component.targetMin != null && component.targetMax != null ? (
      <>
        <ComponentDisplay
          componentType={component.componentType}
          value={component.targetMin}
          currency="USD"
          currencyDecimals={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
          decimalPlaces={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
        />
        {' - '}
        <ComponentDisplay
          componentType={component.componentType}
          value={component.targetMax}
          currency="USD"
          currencyDecimals={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
          decimalPlaces={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
        />
      </>
    ) : (
      ''
    )

  const isTeamLevel = component.isOrganizationLevel
  const isRowReadOnly = isTeamLevel || reviewOnly

  if (isComposite && compositeValues.length > 0) {
    return (
      <>
        <TableRow>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">{component.label}</Typography>
              {!reviewOnly && compositeValues.length > 0 && (
                <IconButton
                  onClick={() => setExpanded(!expanded)}
                  size="small"
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: theme.transitions.create('transform'),
                  }}
                >
                  <ExpandMore />
                </IconButton>
              )}
            </Box>
          </TableCell>
          <TableCell>{periodLabel ?? ''}</TableCell>
          <TableCell>
            {isTeamLevel || reviewOnly ? (
              <ComponentDisplay
                componentType={component.componentType}
                value={reviewOnly ? target.value : overallValue}
                currency="USD"
                currencyDecimals={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
                decimalPlaces={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
                typographyComponent="div"
                fontWeight="normal"
                color="inherit"
                {...(reviewOnly
                  ? {
                      sourceType: component.sourceType,
                      compositeFunction: getCompositeFunction(component.sourceInfo),
                      componentLabel: component.label,
                      periodLabel: periodLabel,
                      isTarget: true,
                    }
                  : {})}
              />
            ) : (
              <ComponentField
                componentType={component.componentType}
                label={`Enter overall target for ${component.label}`}
                value={overallValue}
                onChange={(value) => handleOverallTargetChange(value.toString())}
                name={`overall_${componentId}_${target.periodId}`}
                currency="USD"
                currencyDecimals={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
                decimalPlaces={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
                disabled={false}
                placeholder=""
                shrink
                pill
              />
            )}
          </TableCell>
        </TableRow>
        {expanded && !reviewOnly && (
          <TableRow>
            <TableCell colSpan={3} sx={{ py: 2 }}>
              <Collapse in={expanded && !reviewOnly}>
                <Paper sx={{ ml: 2, mr: 1, p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Composite Values
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width="25%">Label</TableCell>
                          <TableCell width="40%">Description</TableCell>
                          <TableCell width="35%">Target Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {compositeValues.map((item, valueIndex) => (
                          <TableRow key={valueIndex}>
                            <TableCell>
                              <Typography variant="body2">{item.label}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.description}</Typography>
                            </TableCell>
                            <TableCell>
                              {isRowReadOnly ? (
                                <ComponentDisplay
                                  componentType={item.componentType}
                                  value={item.target}
                                  currency="USD"
                                  currencyDecimals={item.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
                                  decimalPlaces={item.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
                                  typographyComponent="div"
                                  fontWeight="normal"
                                  color="inherit"
                                />
                              ) : (
                                <ComponentField
                                  componentType={item.componentType}
                                  label=""
                                  value={item.target}
                                  onChange={(value) =>
                                    handleCompositeValueChange(valueIndex, 'target', value.toString())
                                  }
                                  name={`composite_${valueIndex}_${componentId}_${target.periodId}`}
                                  currency="USD"
                                  currencyDecimals={item.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
                                  decimalPlaces={item.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
                                  disabled={false}
                                  placeholder=""
                                  shrink
                                  pill
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    )
  }

  return (
    <TableRow>
      <TableCell>
        <Typography variant="h6">{component.label}</Typography>
      </TableCell>
      <TableCell>{periodLabel ?? ''}</TableCell>
      <TableCell>
        {component.isOrganizationLevel || reviewOnly ? (
          <ComponentDisplay
            componentType={component.componentType}
            value={target.value}
            currency="USD"
            currencyDecimals={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
            decimalPlaces={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
            typographyComponent="div"
            fontWeight="normal"
            color="inherit"
            sourceType={component.sourceType}
            isTarget={true}
          />
        ) : (
          <div>
            <ComponentField
              componentType={component.componentType}
              label={`Enter value for ${component.label}`}
              value={target.value}
              onChange={(value) => handleUpdateTarget(value, `${componentId}_${target.periodId}`)}
              name={`${componentId}_${target.periodId}`}
              currency="USD"
              currencyDecimals={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
              decimalPlaces={component.componentType === ComponentType.COMPONENT_TYPE_FLOAT ? 2 : 0}
              disabled={false}
              placeholder=""
              shrink
              pill
            />
            {rangeDisplay && (
              <Typography
                variant="caption"
                display="block"
                style={{ color: theme.palette.text.secondary, fontSize: '12px' }}
              >
                Range: {rangeDisplay}
              </Typography>
            )}
          </div>
        )}
      </TableCell>
    </TableRow>
  )
}

interface PlanTargetsProps {
  reviewOnly?: boolean
}

const PlanTargets: React.FC<PlanTargetsProps> = ({ reviewOnly = false }) => {
  const theme = useTheme()
  const { compensationTemplate, plan, targetDTOs, setTargetDTOs } = useCreatePlanContext()

  if (compensationTemplate == null) {
    return <></>
  }

  const componentsMap =
    compensationTemplate != null && compensationTemplate.components != null
      ? compensationTemplate.components.reduce((acc: Record<string, Component>, component) => {
          if (component != null && component.id != null) {
            acc[component.id] = component
          }
          return acc
        }, {})
      : {}

  // Updated to use a composite key of componentId and periodId
  const targetsComponentIdMap = targetDTOs.reduce((acc: Record<string, TargetDTO>, target) => {
    const key = `${target.componentId}_${target.periodId}`
    acc[key] = target
    return acc
  }, {})

  const activeTargetComponentIds = plan.variableAllocations.reduce((acc: Set<string>, table) => {
    const activeTargetsForTable = table.rows.reduce((rowAcc: Set<string>, row) => {
      if (!(row.disabled ?? false)) {
        ;(row.componentIds ?? []).forEach((id) => {
          const periodId = row.periodId // Assuming periodId is available in row
          const compositeKey = `${id}_${periodId}`
          rowAcc.add(compositeKey)
        })
      }
      return rowAcc
    }, new Set<string>())

    activeTargetsForTable.forEach((id) => acc.add(id))
    return acc
  }, new Set<string>())
  // Convert Set back to array if needed elsewhere as array
  const uniqueActiveTargetComponentIds = Array.from(activeTargetComponentIds)

  const periodIdLabelMap = createIdLabelMap(compensationTemplate.period)

  const handleUpdateTarget = (value: string | number, compositeId: string) => {
    const [componentId, periodId] = compositeId.split('_')
    const component = componentsMap[componentId]

    // For composite components, if the value is a JSON string, use it as-is
    // Otherwise, unformat the value as usual
    let targetValue: string
    if (
      component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE &&
      typeof value === 'string' &&
      value.startsWith('{')
    ) {
      // This is a composite JSON structure, use as-is
      targetValue = value
    } else {
      // Regular value, unformat it
      targetValue = unformatValue(value.toString(), component.componentType).toString()
    }

    const newTargets = targetDTOs.map((t) =>
      t.componentId === componentId && t.periodId === periodId ? { ...t, value: targetValue } : t
    )
    setTargetDTOs(newTargets)
  }

  const handleUpdateCompositeTarget = (compositeId: string, compositeValues: CompositeTargetValueItem[]) => {
    const [componentId, periodId] = compositeId.split('_')
    const component = componentsMap[componentId]

    if (component.sourceType !== ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) return

    // Find the existing target to get the current structure
    const existingTarget = targetDTOs.find((t) => t.componentId === componentId && t.periodId === periodId)
    if (!existingTarget) return

    // Parse the existing value to maintain the full structure
    let existingCompositeValue: CompositeTargetValue
    try {
      existingCompositeValue = JSON.parse(existingTarget.value) as CompositeTargetValue
    } catch {
      existingCompositeValue = { value: '0', compositeTargetValues: [] }
    }

    // Create the new composite target value with updated composite values
    const newCompositeValue: CompositeTargetValue = {
      ...existingCompositeValue,
      compositeTargetValues: compositeValues,
    }

    // Update the target with the new composite value
    const newTargets = targetDTOs.map((t) =>
      t.componentId === componentId && t.periodId === periodId ? { ...t, value: JSON.stringify(newCompositeValue) } : t
    )
    setTargetDTOs(newTargets)
  }

  return (
    <Paper sx={{ width: '100%' }}>
      <TableContainer>
        <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead
            sx={{
              '& th': {
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `2px solid ${theme.palette.divider} !important`,
              },
            }}
          >
            <TableRow>
              <TableCell>
                <strong>Target</strong>
              </TableCell>
              <TableCell>
                <strong>Period</strong>
              </TableCell>
              <TableCell>
                <strong>Value</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uniqueActiveTargetComponentIds.map((compositeId) => {
              const [componentId, periodId] = compositeId.split('_')
              const component = componentsMap[componentId]
              const target = targetsComponentIdMap[compositeId]
              const periodLabel = periodIdLabelMap[periodId]

              if (component === undefined) {
                return null
              }

              return (
                <PlanTargetRow
                  key={compositeId}
                  componentId={componentId}
                  component={component}
                  target={target}
                  reviewOnly={reviewOnly}
                  periodLabel={periodLabel}
                  handleUpdateTarget={handleUpdateTarget}
                  handleUpdateCompositeTarget={handleUpdateCompositeTarget}
                />
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

export default PlanTargets
